import { useCallback, useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, WarningOctagon, Shield, Camera } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";
import PhotoUploader from "@/components/PhotoUploader";

const CONTROL_TYPES = [
  { value: "elimination", label: "Eliminación" },
  { value: "substitution", label: "Sustitución" },
  { value: "engineering", label: "Ingeniería" },
  { value: "admin", label: "Administrativo" },
  { value: "epp", label: "EPP" },
];

const LEVEL_STYLE = {
  bajo: "bg-emerald-100 text-emerald-800 border-emerald-200",
  medio: "bg-amber-100 text-amber-800 border-amber-200",
  alto: "bg-orange-100 text-orange-800 border-orange-200",
  critico: "bg-red-100 text-red-800 border-red-200",
};

function riskColor(prob, sev) {
  const s = prob * sev;
  if (s >= 15) return "bg-red-500 text-white";
  if (s >= 8) return "bg-orange-500 text-white";
  if (s >= 4) return "bg-amber-400 text-slate-900";
  return "bg-emerald-400 text-slate-900";
}

const emptyForm = () => ({
  title: "", area: "", activity: "", hazard: "", hazard_id: "", hazard_category: "", project_id: defaultProject(), project_id: defaultProject(),
  exposure: "rutinaria",
  probability: 3, severity: 3, controls: [], residual_probability: 1, residual_severity: 1,
  review_date: "", photos: [],
});

export default function IperModule() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [hazards, setHazards] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const canWrite = ["admin", "manager", "supervisor"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/hse/iper").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/hse/hazards-catalog?active_only=true").then((r) => setHazards(r.data)).catch(() => setHazards([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!assertProject(form.project_id)) return;
    try {
      if (editing) await api.put(`/hse/iper/${editing}`, form);
      else await api.post("/hse/iper", form);
      toast.success("IPER guardado");
      setOpen(false); setEditing(null); setForm(emptyForm()); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar IPER?")) return;
    try { await api.delete(`/hse/iper/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addControl = () => setForm({ ...form, controls: [...form.controls, { type: "admin", description: "", responsible: "" }] });

  return (
    <div className="space-y-6">
      {/* 5×5 heatmap */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={16} weight="duotone" className="text-blue-900" />
          <p className="font-heading font-bold text-slate-900">Matriz de Riesgos 5×5</p>
          <span className="text-xs text-slate-400 ml-auto">Probabilidad (filas) × Severidad (columnas)</span>
        </div>
        <div className="grid grid-cols-6 gap-1 max-w-lg">
          <div />{[1,2,3,4,5].map((s) => <div key={s} className="text-[10px] text-center text-slate-500 uppercase font-bold">S{s}</div>)}
          {[5,4,3,2,1].map((p) => (
            <Fragment key={`row-${p}`}>
              <div className="text-[10px] text-slate-500 uppercase font-bold flex items-center justify-center">P{p}</div>
              {[1,2,3,4,5].map((s) => {
                const count = items.filter((it) => it.probability === p && it.severity === s).length;
                return (
                  <div
                    key={`c-${p}-${s}`}
                    data-testid={`iper-matrix-${p}-${s}`}
                    className={`h-10 rounded flex items-center justify-center text-xs font-bold ${riskColor(p, s)}`}
                    title={`P${p}×S${s} = ${p*s}`}
                  >
                    {count || ""}
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">{items.length} IPER registrados</p>
        {canWrite && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyForm()); } }}>
            <DialogTrigger asChild>
              <Button data-testid="iper-new-button" className="bg-blue-900 hover:bg-blue-800 gap-2"><Plus size={14} weight="bold" /> Nuevo IPER</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar" : "Nuevo"} IPER</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <ProjectField className="col-span-2" value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} testid="iper-project" />
                <div className="col-span-2 space-y-1"><Label className="overline-label">Título</Label>
                  <Input data-testid="iper-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div className="space-y-1"><Label className="overline-label">Área / Nivel</Label>
                  <Input data-testid="iper-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></div>
                <div className="space-y-1"><Label className="overline-label">Actividad</Label>
                  <Input data-testid="iper-activity" value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} /></div>
                <div className="col-span-2 space-y-1"><Label className="overline-label">Peligro (desde catálogo o texto libre)</Label>
                  <Select value={form.hazard_id || ""} onValueChange={(v) => {
                    if (!v) { setForm({ ...form, hazard_id: "", hazard_category: "" }); return; }
                    const h = hazards.find((x) => x.id === v);
                    setForm({
                      ...form,
                      hazard_id: v,
                      hazard: h?.name || form.hazard,
                      hazard_category: h?.category || "",
                      severity: h?.typical_severity ?? form.severity,
                    });
                  }}>
                    <SelectTrigger data-testid="iper-hazard-catalog"><SelectValue placeholder="— Elige del catálogo (Ajustes → Riesgos) —" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64 overflow-y-auto">
                      {hazards.length === 0 && <div className="px-2 py-1 text-xs text-slate-500 italic">Sin peligros en el catálogo. Añádelos en Ajustes → Riesgos.</div>}
                      {hazards.map((h) => (
                        <SelectItem key={h.id} value={h.id}>[{h.category}] {h.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Textarea className="mt-1" data-testid="iper-hazard" value={form.hazard} onChange={(e) => setForm({ ...form, hazard: e.target.value })} rows={2} placeholder="Descripción del peligro (auto-poblada al elegir del catálogo)" />
                </div>
                <div className="space-y-1"><Label className="overline-label">Exposición</Label>
                  <Select value={form.exposure} onValueChange={(v) => setForm({ ...form, exposure: v })}>
                    <SelectTrigger data-testid="iper-exposure"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="rutinaria">Rutinaria</SelectItem>
                      <SelectItem value="no_rutinaria">No rutinaria</SelectItem>
                      <SelectItem value="emergencia">Emergencia</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="overline-label">Revisión</Label>
                  <DateField testid="iper-review-date" value={form.review_date} onChange={(v) => setForm({ ...form, review_date: v })} /></div>
                <div className="col-span-2 border-t border-slate-100 mt-1 pt-3">
                  <p className="overline-label mb-2">Evaluación inicial</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Probabilidad (1-5)</Label>
                      <Input data-testid="iper-prob" type="number" min={1} max={5} value={form.probability} onChange={(e) => setForm({ ...form, probability: Number(e.target.value) })} /></div>
                    <div><Label className="text-xs">Severidad (1-5)</Label>
                      <Input data-testid="iper-sev" type="number" min={1} max={5} value={form.severity} onChange={(e) => setForm({ ...form, severity: Number(e.target.value) })} /></div>
                  </div>
                </div>
                <div className="col-span-2 border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="overline-label">Controles (jerarquía)</p>
                    <Button size="sm" variant="ghost" onClick={addControl} data-testid="iper-add-control" className="h-7 text-xs text-blue-900"><Plus size={12} weight="bold" /> Añadir</Button>
                  </div>
                  <div className="space-y-2">
                    {form.controls.map((c, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-3">
                          <Select value={c.type} onValueChange={(v) => { const n=[...form.controls]; n[idx]={...n[idx],type:v}; setForm({...form,controls:n}); }}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`iper-control-type-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white">{CONTROL_TYPES.map((ct) => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <Input className="col-span-6 h-8 text-xs" placeholder="Descripción" value={c.description} onChange={(e) => { const n=[...form.controls]; n[idx]={...n[idx],description:e.target.value}; setForm({...form,controls:n}); }} data-testid={`iper-control-desc-${idx}`} />
                        <Input className="col-span-2 h-8 text-xs" placeholder="Responsable" value={c.responsible} onChange={(e) => { const n=[...form.controls]; n[idx]={...n[idx],responsible:e.target.value}; setForm({...form,controls:n}); }} />
                        <button className="col-span-1 text-slate-400 hover:text-red-600" onClick={() => setForm({ ...form, controls: form.controls.filter((_, i) => i !== idx) })}><Trash size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-span-2 border-t border-slate-100 pt-3">
                  <p className="overline-label mb-2">Riesgo residual (después de controles)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Probabilidad residual</Label>
                      <Input data-testid="iper-res-prob" type="number" min={1} max={5} value={form.residual_probability} onChange={(e) => setForm({ ...form, residual_probability: Number(e.target.value) })} /></div>
                    <div><Label className="text-xs">Severidad residual</Label>
                      <Input data-testid="iper-res-sev" type="number" min={1} max={5} value={form.residual_severity} onChange={(e) => setForm({ ...form, residual_severity: Number(e.target.value) })} /></div>
                  </div>
                </div>
                <div className="col-span-2 border-t border-slate-100 pt-3">
                  <Label className="overline-label flex items-center gap-1"><Camera size={12} weight="bold" /> Evidencia fotográfica (máx 8)</Label>
                  <PhotoUploader value={form.photos || []} onChange={(photos) => setForm({ ...form, photos })} max={8} testidPrefix="iper-photo" />
                </div>
              </div>
              <Button data-testid="iper-save" onClick={save} disabled={!form.title || !form.hazard} className="bg-blue-900 hover:bg-blue-800 w-full">{editing ? "Actualizar" : "Crear"}</Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full data-table">
          <thead><tr><th>Título</th><th>Peligro</th><th>Área</th><th>Nivel inicial</th><th>Nivel residual</th><th>Revisión</th>{canWrite && <th></th>}</tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8">Sin IPER registrados</td></tr>}
            {items.map((it) => (
              <tr key={it.id} data-testid={`iper-row-${it.id}`}>
                <td className="font-medium">{it.title}</td>
                <td className="text-slate-600 max-w-xs truncate">{it.hazard}</td>
                <td className="text-slate-500 text-xs">{it.area}</td>
                <td><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${LEVEL_STYLE[it.level_initial]}`}>{it.level_initial}</span></td>
                <td><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${LEVEL_STYLE[it.level_residual]}`}>{it.level_residual}</span></td>
                <td className="font-mono text-xs">{it.review_date || "—"}</td>
                {canWrite && (
                  <td className="whitespace-nowrap">
                    <button data-testid={`iper-edit-${it.id}`} onClick={() => { setEditing(it.id); setForm({ title: it.title, area: it.area, activity: it.activity, hazard: it.hazard, exposure: it.exposure, probability: it.probability, severity: it.severity, controls: it.controls || [], residual_probability: it.residual_probability, residual_severity: it.residual_severity, review_date: it.review_date }); setOpen(true); }} className="text-slate-400 hover:text-blue-900 mr-2"><PencilSimple size={14} /></button>
                    <button data-testid={`iper-delete-${it.id}`} onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-500"><Trash size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
